import { Area } from '@app/domain/Area'
import { BehaviorSubject, Observable, map } from 'rxjs'
import { ContextService } from '@services/context.service'
import { Injectable } from '@angular/core'
import { LocalStorageService } from '@services/local-storage-service'
import { ConfigurableDiaryListColumnKey, DiaryListColumnKey, FixedDiaryListColumnKey, StandardDiaryListColumnKey, isQuestionColumnKey } from '../components/diary-list-column-selection/diary-list-column-selection.component'
import { WaitlistEntriesSort } from '../components/waitlist-panel/waitlist-entries-sort'

@Injectable({
    providedIn: 'root',
})
export class DiaryPreferencesService {

    private readonly LIST_VIEW_COLUMNS_VERSION = 3
    private readonly FIXED_LIST_VIEW_COLUMNS_ORDER: FixedDiaryListColumnKey[] = ['status', 'time', 'customer', 'covers', 'reason']
    private readonly CONFIGURABLE_LIST_VIEW_COLUMNS_ORDER: ConfigurableDiaryListColumnKey[] = ['area', 'table', 'details', 'orderDetails', 'customerMessage', 'privateNotes', 'dateCreated']
    private readonly STANDARD_LIST_VIEW_COLUMNS_ORDER: StandardDiaryListColumnKey[] = [
        ...this.FIXED_LIST_VIEW_COLUMNS_ORDER,
        ...this.CONFIGURABLE_LIST_VIEW_COLUMNS_ORDER,
    ]
    private readonly DEFAULT_LIST_VIEW_COLUMNS: DiaryListColumnKey[] = ['status', 'time', 'customer', 'covers', 'reason', 'area', 'table', 'details', 'customerMessage', 'privateNotes']
    _listViewColumns: BehaviorSubject<DiaryListColumnKey[]>
    listViewColumns$!: Observable<DiaryListColumnKey[]>
    _showFinishedBookings: BehaviorSubject<boolean>
    showFinishedBookings$!: Observable<boolean>
    _hidePrivateNotes: BehaviorSubject<boolean>
    hidePrivateNotes$!: Observable<boolean>
    _showBookingSource: BehaviorSubject<boolean>
    showBookingSource$!: Observable<boolean>
    _abbreviateFirstName: BehaviorSubject<boolean>
    abbreviateFirstName$!: Observable<boolean>
    _calendarTableHeight: BehaviorSubject<'extra-small' | 'small' | 'medium' | 'large'>
    calendarTableHeight$!: Observable<'extra-small' | 'small' | 'medium' | 'large'>
    _calendarFitToScreen: BehaviorSubject<boolean>
    calendarFitToScreen$!: Observable<boolean>
    _compactFitToScreen: BehaviorSubject<boolean>
    compactFitToScreen$!: Observable<boolean>
    _defaultReasonId: BehaviorSubject<string | null>
    defaultReasonId$!: Observable<string | null>
    confirmAddBooking$!: Observable<boolean>
    requireChargeByDefault$!: Observable<boolean>
    _confirmMoveBooking: BehaviorSubject<boolean>
    confirmMoveBooking$!: Observable<boolean>
    _walkInDurationTrimPercentage: BehaviorSubject<number>
    walkInDurationTrimPercentage$!: Observable<number>
    _collapsedAreaIds: BehaviorSubject<string[]>
    collapsedAreaIds$!: Observable<string[]>
    _quickActionsEnabled: BehaviorSubject<boolean>
    quickActionsEnabled$!: Observable<boolean>
    _compactToolbar: BehaviorSubject<boolean>
    compactToolbar$!: Observable<boolean>
    _contactAutocompleteEnabled: BehaviorSubject<boolean>
    contactAutocompleteEnabled$!: Observable<boolean>
    pendingPaymentExpiryMinutes$!: Observable<number | null>

    constructor(
        private contextService: ContextService,
        private localStorageService: LocalStorageService
    ) {
        this._listViewColumns = new BehaviorSubject<DiaryListColumnKey[]>(this.getListViewColumns())
        this.listViewColumns$ = this._listViewColumns.asObservable()
        this._showFinishedBookings = new BehaviorSubject<boolean>(this.getShowFinishedBookings())
        this.showFinishedBookings$ = this._showFinishedBookings.asObservable()
        this._hidePrivateNotes = new BehaviorSubject<boolean>(this.getHidePrivateNotes())
        this.hidePrivateNotes$ = this._hidePrivateNotes.asObservable()
        this._showBookingSource = new BehaviorSubject<boolean>(this.getShowBookingSource())
        this.showBookingSource$ = this._showBookingSource.asObservable()
        this._abbreviateFirstName = new BehaviorSubject<boolean>(this.getAbbreviateFirstName())
        this.abbreviateFirstName$ = this._abbreviateFirstName.asObservable()
        this._calendarTableHeight = new BehaviorSubject<'extra-small' | 'small' | 'medium' | 'large'>(this.getCalendarTableHeight())
        this.calendarTableHeight$ = this._calendarTableHeight.asObservable()
        this._calendarFitToScreen = new BehaviorSubject<boolean>(this.getCalendarFitToScreen())
        this.calendarFitToScreen$ = this._calendarFitToScreen.asObservable()
        this._compactFitToScreen = new BehaviorSubject<boolean>(this.getCompactFitToScreen())
        this.compactFitToScreen$ = this._compactFitToScreen.asObservable()
        this._defaultReasonId = new BehaviorSubject<string | null>(this.getDefaultReasonId())
        this.defaultReasonId$ = this._defaultReasonId.asObservable()
        this.confirmAddBooking$ = this.contextService.venue$.pipe(map(venue => venue.diaryPreferences.confirmAddBooking))
        this.requireChargeByDefault$ = this.contextService.venue$.pipe(map(venue => venue.diaryPreferences.merchantBookingsRequireChargeByDefault))
        this._confirmMoveBooking = new BehaviorSubject<boolean>(this.getConfirmMoveBooking())
        this.confirmMoveBooking$ = this._confirmMoveBooking.asObservable()
        this._walkInDurationTrimPercentage = new BehaviorSubject<number>(this.getWalkInDurationTrimPercentage())
        this.walkInDurationTrimPercentage$ = this._walkInDurationTrimPercentage.asObservable()
        this._collapsedAreaIds = new BehaviorSubject<string[]>(this.getCollapsedAreaIds())
        this.collapsedAreaIds$ = this._collapsedAreaIds.asObservable()
        this._quickActionsEnabled = new BehaviorSubject<boolean>(this.getQuickActionsEnabled())
        this.quickActionsEnabled$ = this._quickActionsEnabled.asObservable()
        this._compactToolbar = new BehaviorSubject<boolean>(this.getCompactToolbar())
        this.compactToolbar$ = this._compactToolbar.asObservable()
        this._contactAutocompleteEnabled = new BehaviorSubject<boolean>(this.getContactAutocompleteEnabled())
        this.contactAutocompleteEnabled$ = this._contactAutocompleteEnabled.asObservable()
        this.pendingPaymentExpiryMinutes$ = this.contextService.venue$
            .pipe(map(venue => venue.diaryPreferences.pendingPaymentExpiryMinutes))
    }

    getShowFinishedBookings(): boolean {
        const value = this.localStorageService.getItem('showFinishedBookings')
        if (value === null) {
            return true
        }
        return value === 'true'
    }

    setShowFinishedBookings(showFinishedBookings: boolean) {
        this.localStorageService.setItem('showFinishedBookings', showFinishedBookings.toString())
        this._showFinishedBookings.next(showFinishedBookings)
    }

    getHidePrivateNotes(): boolean {
        const value = this.localStorageService.getItem('hidePrivateNotes')
        if (value === null) {
            return false
        }
        return value === 'true'
    }

    setHidePrivateNotes(hidePrivateNotes: boolean) {
        this.localStorageService.setItem('hidePrivateNotes', hidePrivateNotes.toString())
        this._hidePrivateNotes.next(hidePrivateNotes)
    }

    getShowBookingSource(): boolean {
        const value = this.localStorageService.getItem('showBookingSource')
        if (value === null) {
            return true
        }
        return value === 'true'
    }

    setShowBookingSource(showBookingSource: boolean) {
        this.localStorageService.setItem('showBookingSource', showBookingSource.toString())
        this._showBookingSource.next(showBookingSource)
    }

    getAbbreviateFirstName(): boolean {
        const value = this.localStorageService.getItem('abbreviateFirstName')
        if (value === null) {
            return true
        }
        return value === 'true'
    }

    setAbbreviateFirstName(abbreviateFirstName: boolean) {
        this.localStorageService.setItem('abbreviateFirstName', abbreviateFirstName.toString())
        this._abbreviateFirstName.next(abbreviateFirstName)
    }

    getCalendarTableHeight(): 'extra-small' | 'small' | 'medium' | 'large' {
        const value = this.localStorageService.getItem('calendarTableHeight')
        if (value === null) {
            return 'small'
        }
        return value as 'small' | 'medium' | 'large'
    }

    setCalendarTableHeight(calendarTableHeight: 'extra-small' | 'small' | 'medium' | 'large') {
        this.localStorageService.setItem('calendarTableHeight', calendarTableHeight)
        this._calendarTableHeight.next(calendarTableHeight)
    }

    getCalendarFitToScreen(): boolean {
        const value = this.localStorageService.getItem('calendarFitToScreen')
        if (value === null) {
            return true
        }
        return value === 'true'
    }

    setCalendarFitToScreen(calendarFitToScreen: boolean) {
        this.localStorageService.setItem('calendarFitToScreen', calendarFitToScreen.toString())
        this._calendarFitToScreen.next(calendarFitToScreen)
    }

    getCompactFitToScreen(): boolean {
        const value = this.localStorageService.getItem('compactFitToScreen')
        if (value === null) {
            return true
        }
        return value === 'true'
    }

    setCompactFitToScreen(compactFitToScreen: boolean) {
        this.localStorageService.setItem('compactFitToScreen', compactFitToScreen.toString())
        this._compactFitToScreen.next(compactFitToScreen)
    }

    getDefaultReasonId(): string | null {
        return this.localStorageService.getItem('defaultReasonId') as string | null
    }

    setDefaultReasonId(defaultReasonId: string | null) {
        if (defaultReasonId === null) {
            this.localStorageService.removeItem('defaultReasonId')
        } else {
            this.localStorageService.setItem('defaultReasonId', defaultReasonId)
        }
        this._defaultReasonId.next(defaultReasonId)
    }

    getConfirmMoveBooking(): boolean {
        const value = this.localStorageService.getItem('confirmMoveBooking')
        if (value === null) {
            return true
        }
        return value === 'true'
    }

    setConfirmMoveBooking(confirmMoveBooking: boolean) {
        this.localStorageService.setItem('confirmMoveBooking', confirmMoveBooking.toString())
        this._confirmMoveBooking.next(confirmMoveBooking)
    }

    getWalkInDurationTrimPercentage(): number {
        const value = this.localStorageService.getItem('walkInDurationTrimPercentage')
        if (value === null) {
            return 50
        }
        return value as number
    }

    setWalkInDurationTrimPercentage(walkInDurationTrimPercentage: number) {
        this.localStorageService.setItem('walkInDurationTrimPercentage', walkInDurationTrimPercentage)
        this._walkInDurationTrimPercentage.next(walkInDurationTrimPercentage)
    }

    collapseArea(area: Area) {
        const collapsedAreaIds = this.getCollapsedAreaIds()
        if (!collapsedAreaIds.includes(area.id)) {
            collapsedAreaIds.push(area.id)
            this.setCollapsedAreaIds(collapsedAreaIds)
        }
    }

    expandArea(area: Area) {
        const collapsedAreaIds = this.getCollapsedAreaIds()
        const index = collapsedAreaIds.indexOf(area.id)
        if (index !== -1) {
            collapsedAreaIds.splice(index, 1)
            this.setCollapsedAreaIds(collapsedAreaIds)
        }
    }

    getCollapsedAreaIds(): string[] {
        const value = this.localStorageService.getItem('collapsedAreaIds')
        if (value === null) {
            return []
        }
        return value as string[]
    }

    setCollapsedAreaIds(collapsedAreaIds: string[]) {
        this.localStorageService.setItem('collapsedAreaIds', collapsedAreaIds)
        this._collapsedAreaIds.next(collapsedAreaIds)
    }

    getQuickActionsEnabled(): boolean {
        const value = this.localStorageService.getItem('quickActionsEnabled')
        if (value === null) {
            return false
        }
        return value === 'true'
    }

    setQuickActionsEnabled(quickActionsEnabled: boolean) {
        this.localStorageService.setItem('quickActionsEnabled', quickActionsEnabled.toString())
        this._quickActionsEnabled.next(quickActionsEnabled)
    }

    getCompactToolbar(): boolean {
        const value = this.localStorageService.getItem('compactToolbar')
        if (value === null) {
            return false
        }
        return value === 'true'
    }

    setCompactToolbar(compactToolbar: boolean) {
        this.localStorageService.setItem('compactToolbar', compactToolbar.toString())
        this._compactToolbar.next(compactToolbar)
    }

    getContactAutocompleteEnabled(): boolean {
        const value = this.localStorageService.getItem('contactAutocompleteEnabled')
        if (value === null) {
            return true
        }
        return value === 'true'
    }

    setContactAutocompleteEnabled(contactAutocompleteEnabled: boolean) {
        this.localStorageService.setItem('contactAutocompleteEnabled', contactAutocompleteEnabled.toString())
        this._contactAutocompleteEnabled.next(contactAutocompleteEnabled)
    }

    getWaitlistEntriesSort(): WaitlistEntriesSort | null {
        const value = this.localStorageService.getItem('waitlistEntriesSort')
        if (
            value === null
            || typeof value !== 'object'
            || !('column' in value)
            || !('direction' in value)
            || (
                value.column !== 'startTime'
                && value.column !== 'dateCreated'
                && value.column !== 'numberOfTimesPrompted'
                && value.column !== 'dateLastPrompted'
            )
            || (value.direction !== 'asc' && value.direction !== 'desc')
        ) {
            return null
        }
        return new WaitlistEntriesSort(value.column, value.direction)
    }

    setWaitlistEntriesSort(sort: WaitlistEntriesSort | null) {
        if (sort === null) {
            this.localStorageService.removeItem('waitlistEntriesSort')
        } else {
            this.localStorageService.setItem('waitlistEntriesSort', {
                column: sort.column,
                direction: sort.direction,
            })
        }
    }

    getListViewColumns(): DiaryListColumnKey[] {
        const value = this.localStorageService.getItem('listViewColumns')
        if (value === null || !Array.isArray(value)) {
            return this.DEFAULT_LIST_VIEW_COLUMNS
        }

        const columns = this.normaliseListViewColumns(value)
        const version = this.localStorageService.getItem('listViewColumnsVersion')
        if (version !== this.LIST_VIEW_COLUMNS_VERSION) {
            const migratedColumns = this.migrateListViewColumns(columns, version)
            this.persistListViewColumns(migratedColumns)
            return migratedColumns
        }

        return columns
    }

    setListViewColumns(columns: DiaryListColumnKey[]) {
        const normalisedColumns = this.normaliseListViewColumns(columns)
        this.persistListViewColumns(normalisedColumns)
        this._listViewColumns.next(normalisedColumns)
    }

    private migrateListViewColumns(columns: DiaryListColumnKey[], version: unknown): DiaryListColumnKey[] {
        const numericVersion = typeof version === 'number' ? version : 0
        const selectedFixedColumns = [...this.FIXED_LIST_VIEW_COLUMNS_ORDER]
        const storedConfigurableColumns = columns.filter((columnKey): columnKey is ConfigurableDiaryListColumnKey => {
            return !isQuestionColumnKey(columnKey)
                && this.CONFIGURABLE_LIST_VIEW_COLUMNS_ORDER.includes(columnKey as ConfigurableDiaryListColumnKey)
        })
        const selectedConfigurableColumns = numericVersion < 2
            ? this.CONFIGURABLE_LIST_VIEW_COLUMNS_ORDER.filter(columnKey => {
                if (columnKey === 'details') {
                    return true
                }
                return storedConfigurableColumns.includes(columnKey)
            })
            : storedConfigurableColumns
        const selectedQuestionColumns = columns.filter(columnKey => isQuestionColumnKey(columnKey))
        return [...selectedFixedColumns, ...selectedConfigurableColumns, ...selectedQuestionColumns]
    }

    private persistListViewColumns(columns: DiaryListColumnKey[]) {
        this.localStorageService.setItem('listViewColumns', columns)
        this.localStorageService.setItem('listViewColumnsVersion', this.LIST_VIEW_COLUMNS_VERSION)
    }

    private normaliseListViewColumns(columns: unknown[]): DiaryListColumnKey[] {
        const uniqueColumns = new Set<DiaryListColumnKey>()

        columns.forEach(columnKey => {
            if (this.isSupportedListViewColumnKey(columnKey) && !uniqueColumns.has(columnKey)) {
                uniqueColumns.add(columnKey)
            }
        })

        return Array.from(uniqueColumns)
    }

    private isSupportedListViewColumnKey(columnKey: unknown): columnKey is DiaryListColumnKey {
        if (typeof columnKey !== 'string') {
            return false
        }

        if (isQuestionColumnKey(columnKey)) {
            return true
        }

        return this.STANDARD_LIST_VIEW_COLUMNS_ORDER.includes(columnKey as StandardDiaryListColumnKey)
    }
}
